// OpenTelemetry web SDK for the admin console (#257, ADR 0057) — the same
// mechanism storefront-ui (#186, ADR 0036) uses; one of the two browser
// origins left for it since the original consumer, load-generator-ui
// (#126, ADR 0031), was retired in #262 (ADR 0064).
//
// The point: every real admin action (a price edit, a restock, a
// user-role change, firing test load) starts a span here, the fetch to
// web-bff's `/api/admin/*` carries W3C `traceparent` automatically, and the
// backend it reaches joins the same trace — one trace from the click, with
// no extra plumbing per screen.
//
// Vendor-neutral, like the rest of the repo: OTLP/HTTP exporter + the plain
// OTel API only. The browser can't hold an ingest key — the app is
// same-origin behind its own CloudFront + web-bff origin (ADR 0057), so
// spans export to a relative `/v1/traces`, which the BFF forwards to the
// load-generator's OTLP receiver (services/load-generator/traces.go).
// `traceparent` is injected on same-origin fetches by default.

import { type Attributes, type Span, SpanStatusCode, trace } from "@opentelemetry/api";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { BatchSpanProcessor, WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { ATTR_SERVICE_NAME } from "@opentelemetry/semantic-conventions";

export const TRACER_NAME = "admin-ui";

let started = false;

/**
 * Wire up the web SDK. Call once, before the first render. Idempotent.
 * Exports to a relative `/v1/traces` (the BFF forwards it); in local dev
 * with no BFF that just 404s and spans are dropped — harmless.
 */
export function initBrowserOtel(namespace?: string): void {
  if (started) return;
  started = true;

  const attributes: Attributes = { [ATTR_SERVICE_NAME]: TRACER_NAME };
  if (namespace) attributes["service.namespace"] = namespace;

  const provider = new WebTracerProvider({
    resource: resourceFromAttributes(attributes),
    spanProcessors: [
      new BatchSpanProcessor(new OTLPTraceExporter({ url: "/v1/traces" })),
    ],
  });
  provider.register({ contextManager: new ZoneContextManager() });

  registerInstrumentations({
    instrumentations: [
      new FetchInstrumentation({
        // Same-origin — the default already injects `traceparent` on
        // same-origin fetches, so no propagateTraceHeaderCorsUrls needed.
        // Nothing in this ticket's shell is worth a span on its own (the
        // `/auth/*` session check on load would just be noise); leave the
        // export call and auth/config fetches out. #258+ should extend
        // this list per-endpoint as they add real admin calls that
        // shouldn't get an automatic span of their own (e.g. list/polling
        // GETs), the same way storefront-ui's list grew.
        ignoreUrls: [/\/v1\/traces(\?|$)/, /\/auth\//, /\/config\.json(\?|$)/],
        clearTimingResources: true,
      }),
    ],
  });
}

/**
 * Run `fn` inside a span named `name`, passing it the span so it can add
 * attributes it only learns mid-flight. If the SDK never started (local
 * dev) this is just `fn(noopSpan)` — the OTel API hands back a no-op
 * tracer. For #258+ to wrap their first real mutation (a price edit, a
 * restock, a role change) the same way storefront-ui wraps checkout.
 */
export async function withSpan<T>(
  name: string,
  attributes: Attributes,
  fn: (span: Span) => Promise<T>,
): Promise<T> {
  const tracer = trace.getTracer(TRACER_NAME);
  return tracer.startActiveSpan(name, { attributes }, async (span) => {
    try {
      return await fn(span);
    } catch (err) {
      span.recordException(err as Error);
      span.setStatus({ code: SpanStatusCode.ERROR });
      throw err;
    } finally {
      span.end();
    }
  });
}
